import memoize from 'lodash/memoize';

import FeatureGates from '@atlaskit/feature-gate-js-client';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';
import { type ServedExperiment } from '@atlassian/search-experiment/types';

import { isPartofSainHoldbackExperiment } from './is-partof-sain-holdback-experiment';
import { logger } from './logger';

// eslint-disable-next-line no-barrel-files/no-barrel-files
export { isPartofSainHoldbackExperiment } from './is-partof-sain-holdback-experiment';

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const SAIN_LAYER_NAME = 'kd_sain_layer';
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const SAIN_HELLO_LAYER_NAME = 'kd_sain_hello_layer';
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const JIRA_FPS_NLQ_NAVIGATION_EXPERIMENT_LAYER_NAME = 'rovo_jira_ui_experience_layer';
// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const JIRA_FPS_NLQ_NAVIGATION_TENANTS_EXPERIMENT_LAYER_NAME =
	'rovo_jira_ui_tenants_experience_layer';

function doesLayerFrontendExperimentExist(layerName: string, abTestId: string): boolean {
	const layer = FeatureGates.getLayer(layerName, {
		fireLayerExposure: false,
	});
	const layerFrontendExperimentName = layer.get('frontendExperimentName', null);
	return layerFrontendExperimentName !== null && layerFrontendExperimentName === abTestId;
}

function getLayerFrontendExperimentUnmemoized({
	layerName,
	abTestId,
	onError,
}: {
	layerName: string;
	abTestId: string;
	onError?: (error: Error) => void;
}): ServedExperiment {
	const falseEvaluationResult = { serveExperiment: false, abTestId, cohort: 'not-enrolled' };
	const handleError = (error: Error) => {
		logger.error({ error, functionName: `getLayerFrontendExperimentUnmemoized(${layerName})` });
		onError?.(error);
	};

	try {
		// we skip experiment evaluation if the user is part of the SAIN holdback experiment test cohort,
		// because SAIN is disabled in this case
		if (isPartofSainHoldbackExperiment()) {
			return falseEvaluationResult;
		}
		if (doesLayerFrontendExperimentExist(layerName, abTestId)) {
			return getFrontendExperiment({ abTestId, onError: handleError });
		}
	} catch (error) {
		handleError(error as Error);
	}

	return falseEvaluationResult;
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getSAINLayerFrontendExperiment: ((params: {
	abTestId: string;
	onError?: (error: Error) => void;
}) => ServedExperiment) &
	import('lodash').MemoizedFunction = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendExperimentUnmemoized({ ...params, layerName: SAIN_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getSAINHelloLayerFrontendExperiment: ((params: {
	abTestId: string;
	onError?: (error: Error) => void;
}) => ServedExperiment) &
	import('lodash').MemoizedFunction = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendExperimentUnmemoized({ ...params, layerName: SAIN_HELLO_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getJiraUIExperienceLayerFrontendExperiment: ((params: {
	abTestId: string;
	onError?: (error: Error) => void;
}) => ServedExperiment) &
	import('lodash').MemoizedFunction = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendExperimentUnmemoized({
			...params,
			layerName: JIRA_FPS_NLQ_NAVIGATION_EXPERIMENT_LAYER_NAME,
		}),
	({ abTestId }) => abTestId,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getJiraUIExperienceTenantsLayerFrontendExperiment: ((params: {
	abTestId: string;
	onError?: (error: Error) => void;
}) => ServedExperiment) &
	import('lodash').MemoizedFunction = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendExperimentUnmemoized({
			...params,
			layerName: JIRA_FPS_NLQ_NAVIGATION_TENANTS_EXPERIMENT_LAYER_NAME,
		}),
	({ abTestId }) => abTestId,
);

type ServedMultiArmExperiment = {
	abTestId: string;
	cohort: string;
};

function getLayerFrontendMultiArmExperimentUnmemoized({
	layerName,
	abTestId,
	onError,
}: {
	layerName: string;
	abTestId: string;
	onError?: (error: Error) => void;
}): ServedMultiArmExperiment {
	const handleError = (error: Error) => {
		logger.error({ error, functionName: `getLayerFrontendExperimentUnmemoized(${layerName})` });
		onError?.(error);
	};

	try {
		// we skip experiment evaluation if the user is part of the SAIN holdback experiment test cohort,
		// because SAIN is disabled in this case
		if (
			!isPartofSainHoldbackExperiment() &&
			doesLayerFrontendExperimentExist(layerName, abTestId)
		) {
			const experiment = FeatureGates.getExperiment(abTestId);
			const cohort = experiment.get('cohort', 'not-enrolled');
			return {
				abTestId,
				cohort,
			};
		}
	} catch (error) {
		handleError(error as Error);
	}

	return { abTestId, cohort: 'not-enrolled' };
}

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getSAINLayerFrontendMutiArmExperiment: ((params: {
	abTestId: string;
	onError?: (error: Error) => void;
}) => ServedMultiArmExperiment) &
	import('lodash').MemoizedFunction = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendMultiArmExperimentUnmemoized({ ...params, layerName: SAIN_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getSAINHelloLayerFrontendMutiArmExperiment: ((params: {
	abTestId: string;
	onError?: (error: Error) => void;
}) => ServedMultiArmExperiment) &
	import('lodash').MemoizedFunction = memoize(
	(params: { abTestId: string; onError?: (error: Error) => void }) =>
		getLayerFrontendMultiArmExperimentUnmemoized({ ...params, layerName: SAIN_HELLO_LAYER_NAME }),
	({ abTestId }) => abTestId,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getSAINLayerParamValue: ((paramName: string) => null) &
	import('lodash').MemoizedFunction = memoize(
	(paramName: string) => {
		const layer = FeatureGates.getLayer(SAIN_LAYER_NAME);
		return layer.get(paramName, null);
	},
	(paramName) => paramName,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const hasSAINLayerParamValue: (
	paramName: string,
	paramValue: string,
) => paramValue is never = (paramName: string, paramValue: string) => {
	return getSAINLayerParamValue(paramName) === paramValue;
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const getSAINHelloLayerParamValue: ((paramName: string) => null) &
	import('lodash').MemoizedFunction = memoize(
	(paramName: string) => {
		const layer = FeatureGates.getLayer(SAIN_HELLO_LAYER_NAME);
		return layer.get(paramName, null);
	},
	(paramName) => paramName,
);

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const hasSAINHelloLayerParamValue: (
	paramName: string,
	paramValue: string,
) => paramValue is never = (paramName: string, paramValue: string) => {
	return getSAINHelloLayerParamValue(paramName) === paramValue;
};
